import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Tag, Button, Table, Spin, Empty, Input, Select,
  Space, Divider, Tabs, Modal, Drawer, Descriptions,
  Alert, message, Badge, Card, Popconfirm, Tooltip, Statistic,
} from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  LockOutlined, UnlockOutlined, NumberOutlined, EyeOutlined,
  SearchOutlined, ReloadOutlined, InboxOutlined, DollarOutlined,
  FileTextOutlined, FilePdfOutlined, DownloadOutlined, LoadingOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;
const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CATEGORY_LABELS = {
  material: 'Materials', labor_supply: 'Labor Supply', sub_contractor: 'Sub-Contractors',
  heavy_equipment_rental: 'Heavy Equipment Rental', equipment_rental: 'Equipment Rental',
  fees_and_charges: 'Fees and Charges', repairs_and_maintenance: 'Repairs and Maintenance',
};

const STATUS_TAG_COLOR = { pending_review: 'gold', under_review: 'blue', approved: 'green', rejected: 'red' };
const STATUS_LABELS = { pending_review: 'Pending Review', under_review: 'Under Review', approved: 'Approved', rejected: 'Rejected' };

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatDate = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai' });
};


const BatchManagementView = ({ username, onViewSubmission }) => {
  const [batchSummaries, setBatchSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  // Drill-down
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSubmissions, setBatchSubmissions] = useState([]);
  const [batchInfo, setBatchInfo] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);

  // Receive action
  const [receiveRemarks, setReceiveRemarks] = useState('');
  const [receiveLoading, setReceiveLoading] = useState(false);

  // PDF download
  const [downloadingBatch, setDownloadingBatch] = useState(null);

  // Excel download
  const [downloadingExcel, setDownloadingExcel] = useState(null);

  const fetchBatchSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/summary`);
      if (res.data.success) setBatchSummaries(res.data.summaries || []);
    } catch (err) {
      console.error('Failed to fetch batch summaries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatchSummaries(); }, [fetchBatchSummaries]);

  const openBatchDetail = async (batchNo) => {
    setSelectedBatch(batchNo);
    setBatchLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/submissions`);
      if (res.data.success) {
        setBatchSubmissions(res.data.submissions || []);
        setBatchInfo({
          batch_no: batchNo,
          batch_status: res.data.batch_status,
          batch_received_by: res.data.batch_received_by,
          batch_received_at: res.data.batch_received_at,
        });
      }
    } catch (err) {
      message.error('Failed to load batch submissions');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleMarkReceived = async (batchNo) => {
    setReceiveLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/batches/${batchNo}/receive`, {
        received_by: username,
        remarks: receiveRemarks.trim(),
      });
      if (res.data.success) {
        message.success(`Batch ${batchNo} marked as received`);
        setReceiveRemarks('');
        fetchBatchSummaries();
        if (selectedBatch === batchNo) openBatchDetail(batchNo);
      } else {
        message.error(res.data.message || 'Failed to mark batch');
      }
    } catch (err) {
      message.error('Failed to mark batch as received');
    } finally {
      setReceiveLoading(false);
    }
  };

  const handleReopenBatch = async (batchNo) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/batches/${batchNo}/reopen`, {
        unmarked_by: username,
      });
      if (res.data.success) {
        message.success(`Batch ${batchNo} reopened for uploads`);
        fetchBatchSummaries();
        if (selectedBatch === batchNo) openBatchDetail(batchNo);
      } else {
        message.error(res.data.message || 'Failed to reopen batch');
      }
    } catch (err) {
      message.error('Failed to reopen batch');
    }
  };

  // ── PDF DOWNLOAD ──
  const handleDownloadReport = async (batchNo) => {
    setDownloadingBatch(batchNo);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/procurement/batches/${batchNo}/report/pdf`,
        { responseType: 'blob', timeout: 60000 }
      );

      let filename = `${batchNo}-report.pdf`;
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      const customFilename = res.headers['x-pdf-filename'];
      if (customFilename) filename = customFilename;

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Downloaded report: ${filename}`);
    } catch (err) {
      console.error('PDF download failed:', err);
      if (err.response?.status === 404) {
        message.warning(`No invoices found in Batch ${batchNo}`);
      } else {
        message.error('Failed to download batch report');
      }
    } finally {
      setDownloadingBatch(null);
    }
  };

  // ── EXCEL DOWNLOAD ──
  const handleDownloadExcel = async (batchNo) => {
    setDownloadingExcel(batchNo);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/procurement/batches/${batchNo}/report/excel`,
        { responseType: 'blob', timeout: 60000 }
      );

      let filename = `Batch-${batchNo}-Invoices-Report.xlsx`;
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      const customFilename = res.headers['x-excel-filename'];
      if (customFilename) filename = customFilename;

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Downloaded: ${filename}`);
    } catch (err) {
      console.error('Excel download failed:', err);
      if (err.response?.status === 404) {
        message.warning(`No invoices found in Batch ${batchNo}`);
      } else {
        message.error('Failed to download Excel report');
      }
    } finally {
      setDownloadingExcel(null);
    }
  };

  // Filter
  const filteredSummaries = searchText
    ? batchSummaries.filter(b => String(b.batch_no).includes(searchText))
    : batchSummaries;

  // Stats
  const totalBatches = batchSummaries.length;
  const receivedBatches = batchSummaries.filter(b => b.batch_status === 'received').length;
  const openBatches = totalBatches - receivedBatches;
  const totalInvoices = batchSummaries.reduce((s, b) => s + b.total_submissions, 0);
  const totalValue = batchSummaries.reduce((s, b) => s + b.total_value, 0);

  const batchColumns = [
    {
      title: 'Batch', dataIndex: 'batch_no', key: 'batch_no', width: 100,
      sorter: (a, b) => a.batch_no - b.batch_no,
      render: (v) => (
        <Button type="link" onClick={() => openBatchDetail(v)} style={{ fontWeight: 700, fontSize: 14, padding: 0 }}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Status', dataIndex: 'batch_status', key: 'status', width: 130,
      render: (s, record) => s === 'received'
        ? <Tag icon={<LockOutlined />} color="error" style={{ borderRadius: 4, fontSize: 11 }}>Received</Tag>
        : <Tag icon={<UnlockOutlined />} color="success" style={{ borderRadius: 4, fontSize: 11 }}>Open</Tag>,
    },
    {
      title: 'Invoices', dataIndex: 'total_submissions', key: 'total', width: 90, align: 'center',
      sorter: (a, b) => a.total_submissions - b.total_submissions,
      render: (v) => <Text strong style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Pending', dataIndex: 'pending_count', key: 'pending', width: 80, align: 'center',
      render: (v) => <Text style={{ color: v > 0 ? '#d48806' : '#ccc', fontWeight: v > 0 ? 600 : 400, fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Approved', dataIndex: 'approved_count', key: 'approved', width: 80, align: 'center',
      render: (v) => <Text style={{ color: v > 0 ? '#389e0d' : '#ccc', fontWeight: v > 0 ? 600 : 400, fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Rejected', dataIndex: 'rejected_count', key: 'rejected', width: 80, align: 'center',
      render: (v) => <Text style={{ color: v > 0 ? '#cf1322' : '#ccc', fontWeight: v > 0 ? 600 : 400, fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Total Value', dataIndex: 'total_value', key: 'value', width: 160, align: 'right',
      sorter: (a, b) => a.total_value - b.total_value,
      render: (v) => <Text strong style={{ fontSize: 12, color: '#1a3353' }}>{formatAmount(v)}</Text>,
    },
    {
      title: 'Last Submission', dataIndex: 'last_submission', key: 'last', width: 130,
      render: (v) => <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{formatDate(v)}</Text>,
    },
    {
      title: 'Received By', dataIndex: 'received_by', key: 'recv_by', width: 130,
      render: (v) => v ? <Text style={{ fontSize: 11, color: '#595959' }}>{v}</Text> : <Text style={{ fontSize: 11, color: '#ccc' }}>—</Text>,
    },
    {
      title: 'Action', key: 'action', width: 280, fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" onClick={() => openBatchDetail(record.batch_no)} style={{ borderRadius: 4, fontSize: 11 }}>View</Button>
          <Tooltip title="Download PDF Report">
            <Button
              size="small"
              icon={downloadingBatch === record.batch_no ? <LoadingOutlined spin /> : <FilePdfOutlined />}
              onClick={() => handleDownloadReport(record.batch_no)}
              loading={downloadingBatch === record.batch_no}
              disabled={record.total_submissions === 0}
              style={{ borderRadius: 4, fontSize: 11, color: '#cf1322', borderColor: '#ffa39e' }}
            >
              PDF
            </Button>
          </Tooltip>
          <Tooltip title="Download Excel Report">
            <Button
              size="small"
              icon={downloadingExcel === record.batch_no ? <LoadingOutlined spin /> : <FileExcelOutlined />}
              onClick={() => handleDownloadExcel(record.batch_no)}
              loading={downloadingExcel === record.batch_no}
              disabled={record.total_submissions === 0}
              style={{ borderRadius: 4, fontSize: 11, color: '#389e0d', borderColor: '#b7eb8f' }}
            >
              Excel
            </Button>
          </Tooltip>
          {record.batch_status !== 'received' ? (
            <Popconfirm title={`Mark Batch ${record.batch_no} as received?`}
              description="This will block further uploads to this batch."
              onConfirm={() => handleMarkReceived(record.batch_no)} okText="Mark Received" cancelText="Cancel">
              <Button size="small" type="primary" style={{ borderRadius: 4, fontSize: 11, background: '#389e0d', borderColor: '#389e0d' }}>Mark Received</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title={`Reopen Batch ${record.batch_no}?`}
              description="This will allow new uploads to this batch again."
              onConfirm={() => handleReopenBatch(record.batch_no)} okText="Reopen" cancelText="Cancel">
              <Button size="small" style={{ borderRadius: 4, fontSize: 11 }}>Reopen</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const submissionColumns = [
    { title: 'Ref', dataIndex: 'submission_ref', key: 'ref', width: 135,
      render: (text, record) => <a onClick={() => onViewSubmission && onViewSubmission(record.id)} style={{ fontWeight: 600, fontSize: 13, color: '#1a6ed8', cursor: 'pointer' }}>{text}</a> },
    { title: 'Category', dataIndex: 'category', key: 'cat', width: 145,
      render: (cat) => <span style={{ fontSize: 12, color: '#333' }}>{CATEGORY_LABELS[cat] || cat || '-'}</span> },
    { title: 'LPO', dataIndex: 'lpo_number', key: 'lpo', width: 85,
      render: (t) => <span style={{ fontSize: 12, fontWeight: 500, color: '#333' }}>{t}</span> },
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor', ellipsis: true, width: 200,
      render: (t) => <span style={{ fontSize: 12, color: t ? '#1a1a1a' : '#bbb' }}>{t || '-'}</span> },
    { title: 'Invoice No.', dataIndex: 'invoice_number', key: 'inv', width: 130,
      render: (t) => t ? <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>{t}</Tag> : '-' },
    { title: 'Invoice Date', dataIndex: 'invoice_date', key: 'date', width: 110,
      render: (t) => <span style={{ fontSize: 11, color: '#595959' }}>{t || '-'}</span> },
    { title: 'Total', dataIndex: 'total_amount', key: 'amount', width: 140, align: 'right',
      render: (val) => <span style={{ fontWeight: 600, fontSize: 12, color: '#1a1a1a' }}>{formatAmount(val)}</span> },
    { title: 'VAT', dataIndex: 'vat_amount', key: 'vat', width: 100, align: 'right',
      render: (val) => <span style={{ fontSize: 11, color: '#595959' }}>{formatAmount(val)}</span> },
    { title: 'TRN', dataIndex: 'vendor_trn', key: 'trn', width: 150,
      render: (t) => t ? <span style={{ fontSize: 10, color: '#1a6ed8' }}>{t}</span> : <span style={{ color: '#ccc' }}>—</span> },
    { title: 'DN No.', dataIndex: 'dn_number', key: 'dn', width: 100,
      render: (t) => <span style={{ fontSize: 11, color: '#595959' }}>{t || '—'}</span> },
    { title: 'DN Date', dataIndex: 'dn_date', key: 'dn_date', width: 100,
      render: (t) => <span style={{ fontSize: 11, color: '#595959' }}>{t || '—'}</span> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (s) => <Tag color={STATUS_TAG_COLOR[s] || 'default'} style={{ borderRadius: 3, fontSize: 11 }}>{STATUS_LABELS[s] || s}</Tag> },
    { title: 'Submitted By', dataIndex: 'submitted_by', key: 'user', width: 130, ellipsis: true,
      render: (t) => <span style={{ fontSize: 11, color: '#666' }}>{t}</span> },
    { title: 'Submitted', dataIndex: 'submitted_at', key: 'at', width: 100,
      render: (v) => <span style={{ fontSize: 11, color: '#888' }}>{formatDate(v)}</span> },
  ];

  // ── Batch Detail Drawer ──
  const renderBatchDrawer = () => {
    if (!selectedBatch) return null;
    const isReceived = batchInfo?.batch_status === 'received';

    return (
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NumberOutlined style={{ color: '#1a6ed8', fontSize: 16 }} />
            <span style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a' }}>Batch {selectedBatch}</span>
            {isReceived
              ? <Tag icon={<LockOutlined />} color="error" style={{ borderRadius: 4 }}>Received</Tag>
              : <Tag icon={<UnlockOutlined />} color="success" style={{ borderRadius: 4 }}>Open</Tag>
            }
            <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{batchSubmissions.length} invoices</span>
          </div>
        }
        placement="right" open={!!selectedBatch}
        onClose={() => { setSelectedBatch(null); setBatchSubmissions([]); setBatchInfo(null); }}
        width="90vw" styles={{ body: { padding: 0 } }} destroyOnClose
        extra={
          <Space>
            <Tooltip title="Download PDF Report">
              <Button
                icon={downloadingBatch === selectedBatch ? <LoadingOutlined spin /> : <FilePdfOutlined />}
                size="small"
                onClick={() => handleDownloadReport(selectedBatch)}
                loading={downloadingBatch === selectedBatch}
                disabled={batchSubmissions.length === 0}
                style={{ borderRadius: 4, color: '#cf1322', borderColor: '#ffa39e' }}
              >
                PDF
              </Button>
            </Tooltip>
            <Tooltip title="Download Excel Report">
              <Button
                icon={downloadingExcel === selectedBatch ? <LoadingOutlined spin /> : <FileExcelOutlined />}
                size="small"
                onClick={() => handleDownloadExcel(selectedBatch)}
                loading={downloadingExcel === selectedBatch}
                disabled={batchSubmissions.length === 0}
                style={{ borderRadius: 4, color: '#389e0d', borderColor: '#b7eb8f' }}
              >
                Excel
              </Button>
            </Tooltip>
            {!isReceived ? (
              <Popconfirm title={`Mark Batch ${selectedBatch} as received?`}
                description="This will block further uploads."
                onConfirm={() => handleMarkReceived(selectedBatch)} okText="Mark Received">
                <Button type="primary" size="small" style={{ borderRadius: 4, background: '#389e0d', borderColor: '#389e0d' }}>Mark Received</Button>
              </Popconfirm>
            ) : (
              <Popconfirm title={`Reopen Batch ${selectedBatch}?`}
                onConfirm={() => handleReopenBatch(selectedBatch)} okText="Reopen">
                <Button size="small" style={{ borderRadius: 4 }}>Reopen Batch</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        {batchLoading ? (
          <div style={{ padding: 80, textAlign: 'center' }}><Spin size="large" /></div>
        ) : (
          <div>
            {isReceived && batchInfo?.batch_received_by && (
              <Alert type="info" showIcon icon={<LockOutlined />}
                message={`Batch received by ${batchInfo.batch_received_by} on ${formatDateTime(batchInfo.batch_received_at)}`}
                style={{ margin: '16px 20px 0', borderRadius: 6 }} />
            )}

            {/* Summary stats */}
            <div style={{ display: 'flex', gap: 16, padding: '16px 20px', flexWrap: 'wrap' }}>
              <Card size="small" style={{ flex: 1, minWidth: 120, borderRadius: 6 }}><Statistic title="Total Invoices" value={batchSubmissions.length} valueStyle={{ fontSize: 20, fontWeight: 700 }} /></Card>
              <Card size="small" style={{ flex: 1, minWidth: 120, borderRadius: 6 }}><Statistic title="Pending" value={batchSubmissions.filter(s => s.status === 'pending_review').length} valueStyle={{ fontSize: 20, fontWeight: 700, color: '#d48806' }} /></Card>
              <Card size="small" style={{ flex: 1, minWidth: 120, borderRadius: 6 }}><Statistic title="Approved" value={batchSubmissions.filter(s => s.status === 'approved').length} valueStyle={{ fontSize: 20, fontWeight: 700, color: '#389e0d' }} /></Card>
              <Card size="small" style={{ flex: 1, minWidth: 120, borderRadius: 6 }}><Statistic title="Total Value" value={formatAmount(batchSubmissions.reduce((s, b) => s + (Number(b.total_amount) || 0), 0))} valueStyle={{ fontSize: 16, fontWeight: 700, color: '#1a6ed8' }} /></Card>
            </div>

            <div style={{ padding: '0 20px 20px' }}>
              <Table
                columns={submissionColumns}
                dataSource={batchSubmissions.map(s => ({ ...s, key: s.id }))}
                pagination={batchSubmissions.length > 20 ? { pageSize: 20, size: 'small' } : false}
                size="small" scroll={{ x: 1800 }}
              />
            </div>
          </div>
        )}
      </Drawer>
    );
  };

  // ── MAIN RENDER ──
  return (
    <div>
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, padding: '18px 24px', marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>Batch Management</h2>
        <p style={{ margin: 0, fontSize: 13, color: '#888' }}>View invoices by batch, mark batches as received, download PDF & Excel reports</p>
      </div>

      {/* Stats */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, display: 'flex', flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { label: 'Active Batches', value: totalBatches },
          { label: 'Open', value: openBatches, color: '#389e0d' },
          { label: 'Received', value: receivedBatches, color: '#cf1322' },
          { label: 'Total Invoices', value: totalInvoices, color: '#1a6ed8' },
          { label: 'Total Value', value: formatAmount(totalValue), isText: true },
        ].map((item, idx) => (
          <div key={idx} style={{ flex: '1 1 0', minWidth: 150, padding: '16px 20px', borderRight: idx < 4 ? '1px solid #f0f0f0' : 'none' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
            <div style={{ fontSize: item.isText ? 14 : 22, fontWeight: 700, color: item.color || '#1a1a1a', lineHeight: 1.2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Input placeholder="Search batch number..." value={searchText} onChange={e => setSearchText(e.target.value)}
            allowClear prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            style={{ borderRadius: 4, maxWidth: 250, height: 32, fontSize: 12 }} />
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchBatchSummaries} loading={loading}
            style={{ borderRadius: 4, fontSize: 12 }}>Refresh</Button>
          <Text style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>{filteredSummaries.length} batches</Text>
        </div>

        {loading ? (
          <div style={{ padding: 80, textAlign: 'center' }}><Spin size="large" /></div>
        ) : filteredSummaries.length === 0 ? (
          <Empty style={{ padding: 60 }} description="No batches with submissions found" />
        ) : (
          <Table columns={batchColumns} dataSource={filteredSummaries.map(b => ({ ...b, key: b.batch_no }))}
            pagination={filteredSummaries.length > 20 ? { pageSize: 20, size: 'small', showTotal: t => `${t} batches` } : false}
            size="small" scroll={{ x: 1600 }} />
        )}
      </div>

      {renderBatchDrawer()}
    </div>
  );
};

export default BatchManagementView;